<template>
  <div class="compact-financial-item" :class="itemClass">
    <div class="item-main">
      <!-- Editable Name -->
      <div class="item-name-wrapper">
        <input
          v-if="editingField === 'name'"
          ref="nameInput"
          :value="item.name"
          @input="updateField('name', $event.target.value)"
          @blur="stopEditing"
          @keydown.enter="stopEditing"
          @keydown.escape="stopEditing"
          :placeholder="placeholder"
          class="name-input editing"
        />
        <div
          v-else
          @click.stop="startEditing('name')"
          class="item-name clickable"
          :class="{ empty: !item.name }"
        >
          {{ item.name || placeholder }}
        </div>
      </div>

      <!-- Editable Amount -->
      <div class="item-amount-wrapper">
        <div v-if="editingField === 'amount'" class="currency-input-wrapper">
          <span class="currency-symbol">$</span>
          <input
            ref="amountInput"
            :value="formatAmountForInput"
            @input="handleAmountInput"
            @blur="handleAmountBlur"
            @keydown.enter="stopEditing"
            @keydown.escape="stopEditing"
            type="text"
            placeholder="0"
            class="amount-input editing"
          />
        </div>
        <div
          v-else
          @click="startEditing('amount')"
          class="item-amount clickable"
        >
          {{ formatDisplayAmount }}
        </div>
      </div>
    </div>

    <div class="item-details">
      <!-- START YEAR -->
      <input
        v-if="editingField === 'start_year'"
        ref="startYearInput"
        :value="item.start_year"
        maxlength="4"
        @input="handleYearInput('start', $event)"
        @blur="handleYearBlur('start')"
        @keydown.enter="stopEditing"
        @keydown.escape="stopEditing"
        type="number"
        :class="['year-input', { invalid: startYearInvalid }]"
        style="width:50px"
      />
      <span
        v-else
        class="clickable"
        @click.stop="startEditing('start_year')"
      >{{ item.start_year }}</span>

      <span class="year-separator"> - </span>

      <!-- END YEAR -->
      <input
        v-if="editingField === 'end_year'"
        ref="endYearInput"
        :value="item.end_year"
        maxlength="4"
        @input="handleYearInput('end', $event)"
        @blur="handleYearBlur('end')"
        @keydown.enter="stopEditing"
        @keydown.escape="stopEditing"
        type="number"
        :class="['year-input', { invalid: endYearInvalid }]"
        style="width:50px"
      />
      <span
        v-else
        class="clickable"
        @click.stop="startEditing('end_year')"
      >{{ item.end_year || '∞' }}</span>

      <!-- FREQUENCY -->
      <span v-if="isCashFlow" class="detail-frequency">annual</span>

      <!-- GROWTH RATE -->
      <span
        class="detail-growth clickable"
        @click.stop="startEditing('growth')"
      >
        <input
          v-if="editingField === 'growth'"
          ref="growthInput"
          :value="growthInputValue"
          @input="handleGrowthInput"
          @blur="handleGrowthBlur"
          @keydown.enter="stopEditing"
          @keydown.escape="stopEditing"
          type="number"
          class="growth-input"
          style="width:50px"
        />
        <span v-else>{{ (item.growth_rate * 100).toFixed(2) }}</span>% Growth Rate
      </span>

      <!-- CONTRIBUTION -->
      <span
        v-if="isNetWorth && item.contribution_rate"
        class="detail-contribution clickable"
        @click="startEditing('contribution')"
      >
        <div v-if="editingField === 'contribution'" class="inline-currency-wrapper">
          <span class="currency-symbol">$</span>
          <input
            ref="contributionInput"
            :value="formatContributionForInput"
            @input="handleContributionInput"
            @blur="handleContributionBlur"
            @keydown.enter="stopEditing"
            @keydown.escape="stopEditing"
            type="text"
            class="contribution-input"
            style="width:60px"
          />
        </div>
        <span v-else>{{ formatCurrency(Math.abs(item.contribution_rate)) }}</span>/yr {{ contributionLabel }}
      </span>
    </div>

    <button class="remove-button" @click.stop="$emit('remove', index)" :title="'Remove item'">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'CompactFinancialItem',
  props: {
    item: { type: Object, required: true },
    index: { type: Number, required: true },
    type: {
      type: String,
      required: true,
      validator: v => ['income','expense','asset','liability'].includes(v)
    },
    itemCategory: {
      type: String,
      required: true,
      validator: v => ['cashflow','networth'].includes(v)
    }
  },
  data() {
    return {
      editingField: null,
      startYearInvalid: false,
      endYearInvalid: false,
      growthInputValue: this.item.growth_rate * 100
    };
  },
  computed: {
    isCashFlow() {
      return this.itemCategory === 'cashflow';
    },
    isNetWorth() {
      return this.itemCategory === 'networth';
    },
    itemClass() {
      return `${this.type}-item`;
    },
    placeholder() {
      const placeholders = {
        income: 'e.g. Salary',
        expense: 'e.g. Rent',
        asset: 'e.g. 401k',
        liability: 'e.g. Mortgage'
      };
      return placeholders[this.type] || 'Enter name';
    },
    contributionLabel() {
      return this.item.contribution_rate
        ? (this.type === 'liability' ? 'payment' : 'contribution')
        : '';
    },
    formatDisplayAmount() {
      if (!this.item.amount) return '$0';
      const amt = Math.abs(this.item.amount);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
        notation: amt >= 1e6 ? 'compact' : 'standard'
      }).format(amt);
    },
    formatAmountForInput() {
      return this.item.amount ? this.formatNumberForInput(Math.abs(this.item.amount)) : '';
    },
    formatContributionForInput() {
      return this.item.contribution_rate ? this.formatNumberForInput(Math.abs(this.item.contribution_rate)) : '';
    }
  },
  methods: {
    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
        notation: Math.abs(val) >= 1e6 ? 'compact' : 'standard'
      }).format(Math.abs(val));
    },
    formatNumberForInput(num) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(num);
    },
    parseFormattedNumber(str) {
      const cleaned = str.replace(/[^\d.]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    },
    isYearValid(yearString) {
      // Valid if empty string or exactly 4 digits
      return yearString === '' || yearString.length === 4;
    },
    handleYearInput(which, e) {
      let val = e.target.value.replace(/[^\d]/g, '');
      if (val.length > 4) val = val.slice(0, 4);

      // Immediate validation - check if invalid (not empty and not 4 digits)
      const isInvalid = !this.isYearValid(val);
      
      if (which === 'start') {
        this.startYearInvalid = isInvalid;
      } else {
        this.endYearInvalid = isInvalid;
      }

      const field = which === 'start' ? 'start_year' : 'end_year';
      this.updateField(field, Number(val) || null);
      e.target.value = val;
    },
    handleYearBlur(which) {
      const refName = which === 'start' ? 'startYearInput' : 'endYearInput';
      const inputEl = this.$refs[refName];
      const val = (inputEl?.value || '').toString();
      const isInvalid = !this.isYearValid(val);

      if (which === 'start') {
        this.startYearInvalid = isInvalid;
      } else {
        this.endYearInvalid = isInvalid;
      }

      // Only stop editing if the value is valid
      if (!isInvalid) {
        this.stopEditing();
      }
      // If invalid, keep focus and don't stop editing
    },
    async startEditing(field) {
      this.editingField = field;

      // Clear invalid states when starting to edit
      if (field === 'start_year') this.startYearInvalid = false;
      if (field === 'end_year') this.endYearInvalid = false;

      await this.$nextTick();
      document.addEventListener('click', this.handleClickOutside);

      const refNames = {
        name: 'nameInput',
        amount: 'amountInput',
        start_year: 'startYearInput',
        end_year: 'endYearInput',
        growth: 'growthInput',
        contribution: 'contributionInput'
      };
      
      const refEl = this.$refs[refNames[field]];
      if (refEl) {
        refEl.focus();
        if (field === 'amount' || field === 'contribution') {
          refEl.select();
        } else if (field === 'name') {
          // For name field, select all text for easy replacement
          refEl.select();
        }
      }
    },
    stopEditing() {
      this.editingField = null;
      document.removeEventListener('click', this.handleClickOutside);
      // Clear invalid states when stopping editing
      this.startYearInvalid = false;
      this.endYearInvalid = false;
    },
    handleClickOutside(e) {
      if (this.editingField && !e.target.closest('.compact-financial-item')) {
        this.stopEditing();
      }
    },
    handleGrowthInput(e) {
      this.growthInputValue = e.target.value;
    },
    handleGrowthBlur() {
      const parsed = parseFloat(this.growthInputValue);
      this.updateField('growth_rate', isNaN(parsed) ? 0 : parsed / 100);
      this.stopEditing();
    },
    handleAmountInput(e) {
      this.updateField('amount', this.parseFormattedNumber(e.target.value));
    },
    handleAmountBlur(e) {
      this.updateField('amount', this.parseFormattedNumber(e.target.value));
      this.stopEditing();
    },
    handleContributionInput(e) {
      let v = this.parseFormattedNumber(e.target.value);
      if (this.type === 'liability' && v > 0) v = -v;
      this.updateField('contribution_rate', v);
    },
    handleContributionBlur(e) {
      let v = this.parseFormattedNumber(e.target.value);
      if (this.type === 'liability' && v > 0) v = -v;
      this.updateField('contribution_rate', v);
      this.stopEditing();
    },
    updateField(field, value) {
      this.$emit('update', this.index, field, value);
    }
  }
};
</script>

<style scoped>
.compact-financial-item {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  transition: all 0.2s ease;
  overflow: hidden;
  padding: 0.75rem;
  position: relative;
}

.compact-financial-item:hover {
  border-color: #c0c0c0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Left border color based on item type */
.income-item { border-left: 3px solid #2ecc71; }
.expense-item { border-left: 3px solid #e74c3c; }
.asset-item { border-left: 3px solid #3498db; }
.liability-item { border-left: 3px solid #e67e22; }

/* Main row with name and amount */
.item-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

/* Name styling */
.item-name-wrapper {
  flex: 1;
}

.item-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  line-height: 1.3;
  padding: 1px 3px;
  border-radius: 2px;
  transition: all 0.2s ease;
}

.item-name.empty {
  color: #adb5bd;
  font-style: italic;
}

.name-input {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  border: 1px solid #007bff;
  border-radius: 2px;
  padding: 1px 3px;
  background: #fff;
  width: 100%;
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.25);
}

/* Amount styling */
.item-amount-wrapper {
  min-width: 80px;
}

.item-amount {
  font-weight: 700;
  color: #2c3e50;
  font-size: 0.95rem;
  white-space: nowrap;
  padding: 1px 3px;
  border-radius: 2px;
  transition: all 0.2s ease;
}

.currency-input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.currency-symbol {
  color: #6c757d;
  font-weight: 600;
  margin-right: 1px;
  font-size: 0.85rem;
}

.amount-input {
  font-weight: 700;
  color: #2c3e50;
  font-size: 0.95rem;
  border: 1px solid #007bff;
  border-radius: 2px;
  padding: 1px 3px;
  background: #fff;
  text-align: right;
  min-width: 60px;
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.25);
}

/* Details styling */
.item-details {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.75rem;
}

.item-details > span {
  color: #6c757d;
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  white-space: nowrap;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.item-details > span.clickable:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.detail-years { font-weight: 500; }

/* Frequency dropdown styling */
.frequency-select {
  font-size: 0.75rem;
  color: #2c3e50;
  border: 1px solid #007bff;
  border-radius: 2px;
  padding: 1px 2px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.25);
  min-width: 70px;
}

.year-input, .growth-input, .contribution-input {
  font-size: 0.75rem;
  color: #2c3e50;
  border: 1px solid #007bff;
  border-radius: 2px;
  padding: 1px 2px;
  background: #fff;
  text-align: center;
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.25);
  transition: all 0.2s ease;
}

/* Immediate validation styling for invalid year inputs */
.year-input.invalid {
  border-color: #e74c3c !important;
  box-shadow: 0 0 0 1px rgba(231, 76, 60, 0.3) !important;
  background-color: #fdf2f2 !important;
}

/* Keep the invalid state visible even when focused */
.year-input.invalid:focus {
  border-color: #e74c3c !important;
  box-shadow: 0 0 0 1px rgba(231, 76, 60, 0.3) !important;
  background-color: #fdf2f2 !important;
}

.inline-currency-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.inline-currency-wrapper .currency-symbol {
  font-size: 0.7rem;
}

.contribution-input {
  text-align: right;
}

/* Remove button */
.remove-button {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  background: none;
  border: none;
  color: #adb5bd;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s ease;
  opacity: 0;
}

.compact-financial-item:hover .remove-button {
  opacity: 1;
}

.remove-button:hover {
  color: #e74c3c;
  background: #fff5f5;
}

/* Clickable elements */
.clickable {
  cursor: pointer;
  user-select: none;
}

.clickable:hover {
  background: #f0f8ff !important;
}

/* Remove number input spinners */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield; /* Firefox */
}

/* Input focus styles */
input:focus {
  outline: none;
}

.year-separator {
  color: #adb5bd;
  font-weight: normal;
}

@media (max-width: 768px) {
  .item-main {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .item-details {
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-start;
  }
}
</style>